import React, { useMemo } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { format, parseISO, startOfDay } from 'date-fns';

/**
 * Progress Velocity Chart Component
 * Shows XP accumulation over time with daily, weekly, and monthly views
 */
export function ProgressVelocityChart({ xpEvents, timeRange = 'daily' }) {
  const chartData = useMemo(() => {
    if (!xpEvents || xpEvents?.length === 0) return [];

    // Aggregate XP by time period
    const aggregated = {};
    
    xpEvents?.forEach(event => {
      const date = parseISO(event?.createdAt);
      let key;

      if (timeRange === 'daily') {
        key = format(startOfDay(date), 'MMM dd');
      } else if (timeRange === 'weekly') {
        const weekStart = startOfDay(date);
        weekStart?.setDate(weekStart?.getDate() - weekStart?.getDay());
        key = format(weekStart, 'MMM dd');
      } else {
        key = format(date, 'MMM yyyy');
      }

      if (!aggregated?.[key]) {
        aggregated[key] = {
          date: key,
          xp: 0,
          lessons: 0,
          quizzes: 0
        };
      }

      aggregated[key].xp += event?.amount;
      
      // Categorize by source
      if (event?.source?.includes('lesson')) {
        aggregated[key].lessons += event?.amount;
      } else if (event?.source?.includes('quiz')) {
        aggregated[key].quizzes += event?.amount;
      }
    });

    return Object.values(aggregated)?.sort((a, b) => 
      new Date(a.date) - new Date(b.date)
    );
  }, [xpEvents, timeRange]);

  if (chartData?.length === 0) {
    return (
      <div className="text-center py-12 text-gray-400">
        No XP data available yet. Complete lessons to see your progress!
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <ResponsiveContainer width="100%" height={300}>
        <LineChart data={chartData}>
          <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
          <XAxis 
            dataKey="date" 
            stroke="rgba(255,255,255,0.6)"
            style={{ fontSize: '12px' }}
          />
          <YAxis 
            stroke="rgba(255,255,255,0.6)"
            style={{ fontSize: '12px' }}
          />
          <Tooltip 
            contentStyle={{
              backgroundColor: 'rgba(50, 33, 93, 0.95)',
              border: '1px solid rgba(255,255,255,0.2)',
              borderRadius: '8px',
              color: '#fff'
            }}
          />
          <Legend />
          <Line 
            type="monotone" 
            dataKey="xp" 
            stroke="#FFD700" 
            strokeWidth={3}
            name="Total XP"
            dot={{ fill: '#FFD700', r: 4 }}
          />
          <Line 
            type="monotone" 
            dataKey="lessons" 
            stroke="#00D4FF" 
            strokeWidth={2}
            name="Lesson XP"
            dot={{ fill: '#00D4FF', r: 3 }}
          />
          <Line 
            type="monotone" 
            dataKey="quizzes" 
            stroke="#FF6B9D" 
            strokeWidth={2}
            name="Quiz XP"
            dot={{ fill: '#FF6B9D', r: 3 }}
          />
        </LineChart>
      </ResponsiveContainer>
      <div className="grid grid-cols-3 gap-4 text-center">
        <div>
          <div className="text-2xl font-bold text-yellow-400">
            {chartData?.reduce((sum, d) => sum + d?.xp, 0)}
          </div>
          <div className="text-sm text-gray-400">Total XP</div>
        </div>
        <div>
          <div className="text-2xl font-bold text-blue-400">
            {chartData?.reduce((sum, d) => sum + d?.lessons, 0)}
          </div>
          <div className="text-sm text-gray-400">From Lessons</div>
        </div>
        <div>
          <div className="text-2xl font-bold text-pink-400">
            {chartData?.reduce((sum, d) => sum + d?.quizzes, 0)}
          </div>
          <div className="text-sm text-gray-400">From Quizzes</div>
        </div>
      </div>
    </div>
  );
}